/**
 * Tiptap Editor Wrapper
 *
 * Creates a rich-text WYSIWYG editor for markdown canvas nodes using Tiptap.
 * Handles markdown parsing/serialization and keyboard shortcuts.
 *
 * Toolbar is no longer managed here — the editing system creates a generic
 * toolbar from the entries returned by getToolbar()/onToolbarChange().
 *
 * This is vanilla JS (no Svelte) — fits the "no Svelte inside canvas" constraint.
 */

import { Editor, Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import type { ToolbarEntry } from '../toolbar';

export interface TiptapInstance {
	editor: Editor;
	getMarkdown: () => string;
	getToolbar: () => ToolbarEntry[];
	onToolbarChange: (callback: () => void) => (() => void);
	destroy: () => void;
}

interface TiptapCallbacks {
	onEscape: () => void;
	onSave: () => void;
	onUpdate?: () => void;
}

function handleLinkCommand(editor: Editor) {
	const previousUrl = editor.getAttributes('link').href;
	const url = window.prompt('URL', previousUrl || '');

	if (url === null) return;

	if (url === '') {
		editor.chain().focus().extendMarkRange('link').unsetLink().run();
	} else {
		editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
	}
}

/**
 * Create a Tiptap editor mounted on the given element.
 *
 * @param element - The .note-editor div to mount the editor on
 * @param markdownContent - Raw markdown content to load
 * @param callbacks - onEscape and onSave callbacks
 */
export function createTiptapEditor(
	element: HTMLElement,
	markdownContent: string,
	callbacks: TiptapCallbacks,
): TiptapInstance {
	// Clear the element's current content (rendered markdown HTML)
	element.innerHTML = '';

	// Custom keymap extension for Escape and Cmd+Enter
	const CustomKeymap = Extension.create({
		name: 'customKeymap',
		addKeyboardShortcuts() {
			return {
				Escape: () => {
					callbacks.onEscape();
					return true;
				},
				'Mod-Enter': () => {
					callbacks.onSave();
					return true;
				},
			};
		},
	});

	const editor = new Editor({
		element,
		extensions: [
			StarterKit.configure({
				// Configure Link via StarterKit to avoid duplicate extension registration
				// StarterKit includes Link by default (added in v3)
				link: {
					openOnClick: false,
				},
			}),
			Markdown.configure({
				html: false,
				transformPastedText: true,
			}),
			CustomKeymap,
		],
		content: markdownContent,
		autofocus: 'end',
		editable: true,
		onUpdate: () => {
			callbacks.onUpdate?.();
		},
	});

	// Ensure focus even if autofocus didn't work (e.g. timing issues)
	requestAnimationFrame(() => {
		editor.commands.focus('end');
	});

	// Isolate editor element from canvas input system — stop all events from
	// bubbling up to the canvas container where the input system listens.
	const editorEvents = ['keydown', 'keyup', 'mousedown', 'mouseup', 'click', 'dblclick', 'pointerdown', 'pointerup'] as const;
	function stopBubble(e: Event) {
		e.stopPropagation();
	}
	for (const evt of editorEvents) {
		element.addEventListener(evt, stopBubble);
	}

	// --- Toolbar entries ---

	function getToolbar(): ToolbarEntry[] {
		return [
			{
				type: 'button',
				id: 'bold',
				label: 'B',
				title: 'Bold',
				isActive: editor.isActive('bold'),
				onActivate: () => editor.chain().focus().toggleBold().run(),
			},
			{
				type: 'button',
				id: 'italic',
				label: 'I',
				title: 'Italic',
				isActive: editor.isActive('italic'),
				onActivate: () => editor.chain().focus().toggleItalic().run(),
			},
			{
				type: 'button',
				id: 'strike',
				label: 'S',
				title: 'Strikethrough',
				isActive: editor.isActive('strike'),
				onActivate: () => editor.chain().focus().toggleStrike().run(),
			},
			{
				type: 'button',
				id: 'heading-1',
				label: 'H1',
				title: 'Heading 1',
				isActive: editor.isActive('heading', { level: 1 }),
				onActivate: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
			},
			{
				type: 'button',
				id: 'heading-2',
				label: 'H2',
				title: 'Heading 2',
				isActive: editor.isActive('heading', { level: 2 }),
				onActivate: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
			},
			{
				type: 'button',
				id: 'heading-3',
				label: 'H3',
				title: 'Heading 3',
				isActive: editor.isActive('heading', { level: 3 }),
				onActivate: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
			},
			{
				type: 'button',
				id: 'bullet-list',
				label: '•',
				title: 'Bullet List',
				isActive: editor.isActive('bulletList'),
				onActivate: () => editor.chain().focus().toggleBulletList().run(),
			},
			{
				type: 'button',
				id: 'ordered-list',
				label: '1.',
				title: 'Ordered List',
				isActive: editor.isActive('orderedList'),
				onActivate: () => editor.chain().focus().toggleOrderedList().run(),
			},
			{
				type: 'button',
				id: 'blockquote',
				label: '❝',
				title: 'Blockquote',
				isActive: editor.isActive('blockquote'),
				onActivate: () => editor.chain().focus().toggleBlockquote().run(),
			},
			{
				type: 'button',
				id: 'code',
				label: '<>',
				title: 'Code',
				isActive: editor.isActive('code'),
				onActivate: () => editor.chain().focus().toggleCode().run(),
			},
			{
				type: 'button',
				id: 'link',
				label: '🔗',
				title: 'Link',
				isActive: editor.isActive('link'),
				onActivate: () => handleLinkCommand(editor),
			},
		];
	}

	function onToolbarChange(callback: () => void): () => void {
		editor.on('selectionUpdate', callback);
		editor.on('transaction', callback);
		return () => {
			editor.off('selectionUpdate', callback);
			editor.off('transaction', callback);
		};
	}

	return {
		editor,
		getMarkdown: () => {
			return editor.storage.markdown.getMarkdown();
		},
		getToolbar,
		onToolbarChange,
		destroy: () => {
			for (const evt of editorEvents) {
				element.removeEventListener(evt, stopBubble);
			}
			editor.destroy();
		},
	};
}
